/**
 * Sprint 17 B1: Journey Builder Page — wired to save API, brand-compliant
 * Full-page journey canvas with name, description, status, and save.
 */
import { useState, useCallback } from "react";
import { useTranslation } from "@/i18n";
import { Save } from "lucide-react";
import { JourneyCanvas } from "@/components/journey/journey-canvas";
import { useSaveJourney } from "@/hooks/use-journeys";
import type { JourneyNode, JourneyEdge, JourneyStatus } from "@/components/journey/types";

export default function JourneyBuilderPage() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<JourneyStatus>("draft");
  const [nodes, setNodes] = useState<JourneyNode[]>([]);
  const [edges, setEdges] = useState<JourneyEdge[]>([]);
  const { mutate: saveJourney, isLoading: isSaving } = useSaveJourney();

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    await saveJourney({ name: name.trim(), description: description.trim(), status, nodes, edges });
  }, [name, description, status, nodes, edges, saveJourney]);

  const triggerCount = nodes.filter((n) => n.type === "trigger").length;
  const goalCount = nodes.filter((n) => n.type === "goal").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-3 flex-1 max-w-lg">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("journey.builder.namePlaceholder")}
            className="w-full text-2xl font-semibold bg-transparent border-b border-transparent hover:border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none pb-1 transition-colors text-[var(--color-foreground)]"
          />
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("journey.builder.descriptionPlaceholder")}
            className="w-full text-sm bg-transparent border-b border-transparent hover:border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none pb-1 transition-colors text-[var(--color-muted-foreground)]"
          />
        </div>

        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as JourneyStatus)}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-sm text-[var(--color-foreground)]"
          >
            <option value="draft">{t("journey.status.draft")}</option>
            <option value="active" disabled={triggerCount === 0}>{t("journey.status.active")}</option>
            <option value="paused">{t("journey.status.paused")}</option>
          </select>

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            <Save className="h-4 w-4" />
            {isSaving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex gap-4 text-xs text-[var(--color-muted-foreground)]">
        <span>{nodes.length} {t("journey.builder.nodes")}</span>
        <span>{triggerCount} {t("journey.builder.triggers")}</span>
        <span>{goalCount} {t("journey.builder.goals")}</span>
        <span>{edges.length} {t("journey.builder.connections")}</span>
      </div>

      {/* Canvas */}
      <JourneyCanvas
        nodes={nodes}
        edges={edges}
        onNodesChange={setNodes}
        onEdgesChange={setEdges}
      />
    </div>
  );
}
